export function Privacy() {
  return (
    <article className="mt-6">
      <h2 className="font-display font-black text-3xl mb-2">privacy notice</h2>
      <p className="text-ink-mute text-sm mb-2">last updated: 2026-05-04 · draft for legal review</p>
      <p className="text-ink-soft mb-8">
        This notice explains how we handle personal data under the EU General Data Protection Regulation
        (Regulation 2016/679, &ldquo;GDPR&rdquo;) and the UK GDPR / Data Protection Act 2018.
      </p>

      <Section title="1. who is responsible (controller)">
        <p>
          The data controller is <b>Terradynamic Ltd</b>,
          registered at <b>Solun 47, Sofia, Bulgaria</b>,
          company number <b>BG208660522</b>.
        </p>
        <p>
          Privacy contact: <a className="text-coral-dark underline" href="mailto:privacy@thelivephotos.com">privacy@thelivephotos.com</a>.
          We have not appointed a Data Protection Officer (DPO) as our processing does not currently meet the Art. 37
          GDPR thresholds; this will be re-assessed periodically.
        </p>
        <p>
          We have not appointed a UK representative under Art. 27 UK GDPR; our processing of UK residents&rsquo;
          data is occasional and not on a large scale, and we do not process special-category data of UK
          residents at scale, so the Art. 27(2) exemption applies. UK users may contact us directly using the
          email address above for any data-protection request.
        </p>
      </Section>

      <Section title="2. what we collect and why (categories, purposes, legal bases)">
        <table className="w-full text-sm border-collapse mt-2">
          <thead>
            <tr className="text-left border-b border-ink/10">
              <th className="py-2 pr-2 font-medium">data</th>
              <th className="py-2 pr-2 font-medium">purpose</th>
              <th className="py-2 font-medium">legal basis</th>
            </tr>
          </thead>
          <tbody className="text-ink-soft">
            <Row d="email address (via Amazon Cognito)" p="account creation, sign-in, transactional email" b="contract performance, Art. 6(1)(b)" />
            <Row d="uploaded photo, prompt, generated video" p="run the moderation + animation pipeline you requested; show you your past renders" b="contract performance, Art. 6(1)(b)" />
            <Row d="moderation outputs (approve / reject / category labels)" p="block prohibited content (minors, sexual, non-consensual)" b="legal obligation Art. 6(1)(c) and legitimate interests Art. 6(1)(f) — protecting users and meeting platform-safety duties" />
            <Row d="payment metadata (Stripe customer + checkout session refs, never the card)" p="charge for tokens, prevent fraud, comply with tax/accounting law" b="contract performance Art. 6(1)(b); legal obligation Art. 6(1)(c)" />
            <Row d="server logs (IP, user-agent, timestamps, error events)" p="security, debugging, abuse prevention" b="legitimate interests Art. 6(1)(f)" />
            <Row d="submission IP address (stored on the job row)" p="abuse prevention and forensics in moderation cases" b="legitimate interests Art. 6(1)(f)" />
            <Row d="first-party analytics events (page views, session id, country / device class, named events e.g. job_submitted)" p="aggregate product analytics — improving the service; never shared, never used for advertising" b="legitimate interests Art. 6(1)(f)" />
            <Row d="acceptance of terms (timestamp + version)" p="evidence of consent / agreement" b="legal obligation, legitimate interests" />
            <Row d="complaints you submit" p="handle reports of prohibited content, comply with platform-safety duties" b="legitimate interests; legal obligation" />
          </tbody>
        </table>
        <p className="mt-3">
          We do <b>not</b> use uploaded photos to train AI models. We do not enrich your data with information from
          third parties, and we do not perform automated profiling for advertising.
        </p>
      </Section>

      <Section title="3. automated decision-making (Art. 22 GDPR)">
        <p>
          Every submission is screened by an automated content-moderation model. If it is rejected as prohibited
          (minors, sexual, non-consensual, etc.), we will not run the rendering pipeline. This is an
          <i> automated decision </i> within the meaning of Art. 22 GDPR / UK GDPR. You may request human review of
          a rejection by writing to{" "}
          <a className="text-coral-dark underline" href="mailto:privacy@thelivephotos.com">privacy@thelivephotos.com</a>{" "}
          or via the <a className="text-coral-dark underline" href="/report">report page</a>; we will look at the
          decision and respond within 14 days.
        </p>
      </Section>

      <Section title="4. who else processes your data (recipients)">
        <p>We use the following processors strictly to deliver the service:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><b>Amazon Web Services, Inc.</b> — storage (S3), CDN (CloudFront), authentication (Cognito), DNS (Route53), region: <i>us-east-1 (United States)</i></li>
          <li><b>Stripe, Inc.</b> — payment processing (US/EU)</li>
          <li>self-hosted moderation and rendering models running on infrastructure under our control in <b>Bulgaria</b></li>
        </ul>
        <p>
          We have a Data Processing Agreement / Data Processing Addendum in place with each commercial processor,
          consistent with Art. 28 GDPR.
        </p>
        <p>
          We do not sell personal data. We disclose data outside this list only when required by valid legal
          process or to protect users or the public from imminent harm.
        </p>
      </Section>

      <Section title="5. international transfers">
        <p>
          AWS hosting in <i>us-east-1</i> means some of your data is transferred to the United States. We rely on:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>the <b>EU–US Data Privacy Framework</b> (where AWS is certified) for transfers from the EEA, and</li>
          <li>the <b>UK Extension to the EU–US Data Privacy Framework</b>, or alternatively the UK International Data Transfer Agreement, for transfers from the UK,</li>
          <li>Standard Contractual Clauses (Commission Decision 2021/914) as fall-back, supplemented where necessary.</li>
        </ul>
        <p>
          Stripe transfers are handled under their own SCCs / DPF certification. You can request a copy of the
          relevant transfer mechanism by emailing privacy@thelivephotos.com.
        </p>
      </Section>

      <Section title="6. how long we keep it (retention)">
        <ul className="list-disc pl-6 space-y-1">
          <li>uploaded photos: <b>7 days</b> (S3 lifecycle)</li>
          <li>generated videos (clean + watermarked): <b>30 days</b>, or whenever you delete the job</li>
          <li>job records (prompt, status, moderation outcome): same as the videos</li>
          <li>account record + ledger: until you delete your account, then <b>30 days</b></li>
          <li>payment records and invoices: <b>7 years</b> (statutory bookkeeping retention)</li>
          <li>server / security logs: <b>90 days</b></li>
          <li>complaints: <b>2 years</b>, longer if part of an open investigation</li>
        </ul>
      </Section>

      <Section title="7. your rights">
        <p>Under the GDPR / UK GDPR you have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><b>access</b> a copy of your data (Art. 15)</li>
          <li><b>rectify</b> inaccurate data (Art. 16)</li>
          <li><b>erase</b> your data, subject to legal retention (Art. 17)</li>
          <li><b>restrict</b> processing (Art. 18)</li>
          <li><b>portability</b> of data you provided to us, in a machine-readable format (Art. 20)</li>
          <li><b>object</b> to processing based on legitimate interests (Art. 21)</li>
          <li><b>not be subject to a solely automated decision</b> with significant effects, and to request human review of moderation decisions (Art. 22)</li>
          <li><b>withdraw consent</b> at any time, where processing is based on consent (this does not affect prior lawful processing)</li>
          <li><b>lodge a complaint</b> with a supervisory authority — for EU users, the data protection authority of your member state; our lead supervisory authority is <b>the Bulgarian Commission for Personal Data Protection (cpdp.bg)</b>. UK users may complain to the Information Commissioner&rsquo;s Office (ICO) at <a className="text-coral-dark underline" href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.</li>
        </ul>
        <p>
          To exercise any of these rights write to{" "}
          <a className="text-coral-dark underline" href="mailto:privacy@thelivephotos.com">privacy@thelivephotos.com</a>.
          We respond within one month (extendable by two months for complex requests, with notice). Identity
          verification may be required.
        </p>
      </Section>

      <Section title="8. cookies, local storage, and analytics">
        <p>
          We use a small number of <b>strictly-necessary</b> browser local-storage entries to keep you
          signed in (Cognito tokens) and to give every visit an ephemeral session id used by our analytics.
          Under EU/UK ePrivacy rules these are exempt from the consent requirement, so we do not show a
          cookie banner.
        </p>
        <p>
          We collect <b>first-party analytics</b> on our own infrastructure: anonymous page views,
          session id (lasts only until you close the tab), country and device class derived from
          request headers, and named events such as &ldquo;sign-up complete&rdquo; or
          &ldquo;render submitted&rdquo;. We do <b>not</b> use Google Analytics, Plausible, PostHog,
          Facebook pixel, or any other third-party analytics or advertising tracker. We do not build
          cross-site profiles and we never sell or share this data.
        </p>
      </Section>

      <Section title="9. children's data (Art. 8 GDPR)">
        <p>
          The service is not intended for children. You must be at least 18 to use it; we collect an explicit
          confirmation of age at first sign-in. We do not knowingly collect data from anyone under 18; if you
          believe a child&rsquo;s data has been provided, contact us immediately and we will erase it.
        </p>
      </Section>

      <Section title="10. data security">
        <p>
          Data in transit is protected with TLS 1.2+. Data at rest is encrypted (AWS S3 SSE, Postgres at-rest
          encryption). Access is restricted to a minimal set of operators with authentication. We will notify
          affected users and the relevant supervisory authority of any personal-data breach in accordance with
          Art. 33 / 34 GDPR.
        </p>
      </Section>

      <Section title="11. changes">
        <p>
          We may update this notice. Material changes will be announced by email and require re-acceptance before
          you can submit further renders.
        </p>
      </Section>

      <p className="text-xs text-ink-mute mt-10">
        This is a draft. Have a qualified data-protection lawyer in your jurisdiction review before launch,
        especially the lawful-basis selections, the international-transfer mechanism, and the lead supervisory
        authority designation.
      </p>
    </article>
  );
}

function Row({ d, p, b }: { d: string; p: string; b: string }) {
  return (
    <tr className="border-b border-ink/5 align-top">
      <td className="py-2 pr-2">{d}</td>
      <td className="py-2 pr-2">{p}</td>
      <td className="py-2">{b}</td>
    </tr>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-7">
      <h3 className="font-display font-bold text-xl mb-2">{title}</h3>
      <div className="space-y-2 text-ink-soft">{children}</div>
    </section>
  );
}
